/**
 * Token-consume landing page. The admin lands here in a new tab after
 * clicking "Open as client" — we POST the token to the backend, which
 * sets the impersonation cookies, then we forward to /dashboard.
 */
import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AuthImpersonate() {
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  // useRef guards against the React 18 StrictMode double-mount which
  // would otherwise consume the (one-shot) token twice.
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    const token = params.get("token");
    if (!token) {
      setError("Missing impersonation token.");
      return;
    }
    api
      .post("/auth/impersonate", { token })
      .then(() => setDone(true))
      .catch((err) =>
        setError(
          formatApiErrorDetail(err?.response?.data?.detail) ||
            "Failed to start impersonation.",
        ),
      );
  }, [params]);

  if (done) {
    // Hard navigate so the AuthProvider re-fetches /auth/me with the
    // newly-set target-user cookies.
    window.location.replace("/dashboard");
    return null;
  }
  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
        data-testid="impersonate-error"
      >
        <div className="max-w-md text-center">
          <h1 className="text-xl font-medium text-slate-900">
            Impersonation failed
          </h1>
          <p className="mt-2 text-sm text-rose-700">{error}</p>
          <p className="mt-4 text-xs text-slate-500">
            Ask the admin who sent this link to generate a fresh one
            (tokens expire after 5 minutes and can only be used once).
          </p>
          <Navigate to="/login" replace />
        </div>
      </div>
    );
  }
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50"
      data-testid="impersonate-loading"
    >
      <div className="flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Signing you in as the
        client…
      </div>
    </div>
  );
}
